import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip.tsx"
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

type NavButtonProps = {
  icon: JSX.Element,
  routeTo: string,
  description: string,
  name: string,
  searchValue?: string
}

const NavButton = ({ icon, routeTo, description, name, searchValue }: NavButtonProps) => {
  const [hovered, setHovered] = useState(false)
  
   const location = useLocation()
  
  const isCustomActive = () => {
    const currentPath = location.pathname
    const currentSearch = location.search
    
    const [targetPath, targetSearch] = routeTo.split('?')
    
    if (currentPath !== targetPath) return false
    
    if (!targetSearch && !searchValue) return true
    
    if (searchValue) {
      const urlParams = new URLSearchParams(currentSearch)
      const targetParams = new URLSearchParams(targetSearch || '')
      
      for (const [key, value] of targetParams.entries()) {
        if (urlParams.get(key) !== value) return false
      }
      return true
    }
    
    return currentSearch === `?${targetSearch}`
  }
  
  const isActive = isCustomActive()

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <NavLink
            test-id={routeTo}
            to={routeTo}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            end
            className="w-[3.50rem] relative aspect-square rounded-md h-full text-black"
          >
            {() => (
              <motion.div
                className="w-[3.50rem] relative aspect-square rounded-md h-full flex flex-col items-center justify-center"
              >
                <div className="flex flex-col items-center justify-center h-full gap-1">
                  <motion.div 
                    className="flex-shrink-0"
                    animate={{ 
                      y: (isActive || hovered) ? -4 : 0 
                    }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {icon}
                  </motion.div>
                  
                  <motion.div 
                    className="h-3 flex items-center justify-center text-[0.6rem] leading-3"
                    animate={{ 
                      y: (isActive || hovered) ? -2 : 0 
                    }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <AnimatePresence mode="wait">
                      {(isActive || hovered) && (
                        <motion.span
                          key="nav-text"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                        >
                          {name}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
                
                {/* Active background */}
                {isActive && (
                  <motion.span
                    layoutId="pill-tab-nav"
                    transition={{ type: "spring", duration: 0.5 }}
                    className="absolute inset-0 z-0 rounded-md bg-white/40 border-gray-100 border-2 shadow-white shadow blur-sm"
                  />
                )}
              </motion.div>
            )}
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default NavButton;
